import {
  confirmResetPassword,
  confirmSignUp,
  getCurrentUser,
  resendSignUpCode,
  resetPassword,
  signIn,
  signOut,
  signUp,
} from "aws-amplify/auth";
import { createContext, useCallback, useEffect, useState } from "react";
import type { SignInInput } from "../../routes/SignInPage";
import {
  type CurrentUser,
  type User,
  useCurrentUserRestApi,
} from "../useAuthRestApi";
import { type SignUpStatus, useSignUpStatus } from "./useSignUpStatus";

export type SignInHandler = (input: SignInInput) => Promise<void>;
export type ResetPassWordHandler = (input: ResetPassWordInput) => Promise<void>;
export type SignOutHandler = () => Promise<void>;
export type ClearSignUpStatusHandler = () => void;
export type SignUpHandler = (input: SignUpInput) => Promise<void>;
export type ConfirmSignUpHandler = (input: ConfirmSignUpInput) => Promise<void>;
export type ResendSignUpHandler = () => Promise<void>;
export type ConfirmResetPassWordHandler = (
  input: ConfirmResetPassWordInput,
) => Promise<void>;

interface IAuthContext {
  currentUser: CurrentUser | null;
  isLoadingUser: boolean;
  authError: Error | undefined;
  signUpStatus: SignUpStatus;
  signIn: SignInHandler;
  resetPassWord: ResetPassWordHandler;
  signOut: SignOutHandler;
  signUp: SignUpHandler;
  confirmSignUp: ConfirmSignUpHandler;
  resendSignUp: ResendSignUpHandler;
  confirmResetPassWord: ConfirmResetPassWordHandler;
  /** ユーザー情報を再取得する */
  refetchMe?: () => Promise<User>;
}

export type SignUpInput = {
  familyName: string;
  givenName: string;
  familyNameKana: string;
  givenNameKana: string;
  email: string;
  password: string;
};

export type ConfirmSignUpInput = {
  email: string;
  authCode: number;
};

export type ResetPassWordInput = {
  email: string;
};

export type ConfirmResetPassWordInput = {
  email: string;
  authCode: number;
  newPassword: string;
};

type AuthError = {
  code: string;
  message: string;
};

const AuthErrors: AuthError[] = [
  {
    code: "UsernameExistsException",
    message: "入力されたメールアドレスは既に登録されています",
  },
  {
    code: "InvalidPasswordException",
    message: "パスワードが正しくありません",
  },
  {
    code: "InvalidParameterException",
    message: "有効でないパラメータが入力されています",
  },
  { code: "CodeMismatchException", message: "認証コードが正しくありません。" },
  {
    code: "CodeDeliveryFailureException",
    message: "認証コードの送信に失敗しました。",
  },
  {
    code: "UserLambdaValidationException",
    message:
      "システムエラーが発生しました。恐れいりますが管理者にお問いあわせください。",
  },
  {
    code: "UserNotFoundException",
    message: "メールアドレスまたはパスワードが正しくありません。",
  },
  {
    code: "NotAuthorizedException",
    message: "メールアドレスまたはパスワードが正しくありません。",
  },
  {
    code: "InvalidParameterException",
    message: "メールアドレスまたはパスワードが正しくありません。",
  },
];

const AuthContext = createContext<IAuthContext>({
  currentUser: null,
  isLoadingUser: true,
  authError: undefined,
  signUpStatus: { email: "", confirming: false },
  signIn: async (_: SignInInput) => {
    // nothing to do
  },
  signUp: async (_: SignUpInput) => {
    // nothing to do
  },
  confirmSignUp: async (_: ConfirmSignUpInput) => {
    // nothing to do
  },
  resendSignUp: async () => {
    // nothing to do
  },
  signOut: async () => {
    // nothing to do
  },
  resetPassWord: async (_: ResetPassWordInput) => {
    // nothing to do
  },
  confirmResetPassWord: async (_: ConfirmResetPassWordInput) => {
    // nothing to do
  },
  refetchMe: undefined,
});

const getAuthErrorMessage = (error: unknown): string => {
  // AWS Amplify v6 では error.name にエラーコードが入る
  const code =
    (error as { code?: string })?.code ||
    (error as { name?: string })?.name ||
    "";

  const authError = AuthErrors.find(
    (e: AuthError) => e.code === code || code.includes(e.code),
  );

  if (authError) {
    return authError.message;
  }

  // フォールバック: エラーメッセージがあればそれを返す
  const message = (error as { message?: string })?.message;
  if (message) {
    return message;
  }

  return "エラーが発生しました。しばらくしてから再度お試しください。";
};

// TODO: 直す
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const AuthProvider = (props: any) => {
  const [signUpStatus, setSignUpStatus, clearSignUpStatus] = useSignUpStatus();

  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);

  const {
    data: meData,
    loading: meLoading,
    error: meError,
    refetch: refetchMe,
  } = useCurrentUserRestApi({
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });

  // REST APIレスポンスをCurrentUserに変換するヘルパー
  const convertToCurrentUser = useCallback(
    (user: User): CurrentUser => ({
      id: user.id,
      userId: user.userId,
      organizationId: user.organizationId,
      familyName: user.familyName,
      givenName: user.givenName,
      familyNameKana: user.familyNameKana,
      givenNameKana: user.givenNameKana,
      email: user.email,
      // Python Backend から返される tenants があれば含める
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      tenants: (user as any).tenants,
    }),
    [],
  );

  useEffect(() => {
    if (meData?.me) {
      const userFragment = convertToCurrentUser(meData.me);
      setCurrentUser(userFragment);
    }
  }, [meData, convertToCurrentUser]);

  // 現在の認証状態を確認
  const checkAuthStatus = useCallback(async () => {
    try {
      const session = await getCurrentUser();
      if (session) {
        // REST APIでユーザー情報を取得
        try {
          const userData = await refetchMe();

          // 取得したユーザー情報を保存
          if (userData) {
            const userFragment = convertToCurrentUser(userData);
            setCurrentUser(userFragment);
          }
        } catch (_error) {
          // ユーザー情報取得エラーは無視する
        }
      } else {
        // セッションがない場合はユーザーをクリア
        setCurrentUser(null);
      }
    } catch (_error) {
      setCurrentUser(null);
    }
  }, [refetchMe, convertToCurrentUser]);

  // useEffectでマウント時に認証状態を確認
  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  const onSignIn = useCallback(
    async (input: SignInInput) => {
      try {
        const { isSignedIn } = await signIn({
          username: input.email,
          password: input.password,
        });

        if (isSignedIn) {
          // サインイン後に認証状態を確認
          await checkAuthStatus();
        }
      } catch (error: unknown) {
        throw new Error(getAuthErrorMessage(error));
      }
    },
    [checkAuthStatus],
  );

  const onSignOut = useCallback(async () => {
    await signOut();
  }, []);

  const onResendSignUp = useCallback(async () => {
    await resendSignUpCode({ username: signUpStatus.email });
  }, [signUpStatus]);

  const onSignUp = useCallback(
    async (input: SignUpInput) => {
      try {
        await signUp({
          username: input.email,
          password: input.password,
          options: {
            userAttributes: {
              family_name: input.familyName,
              given_name: input.givenName,
              "custom:family_name_kana": input.familyNameKana,
              "custom:given_name_kana": input.givenNameKana,
            },
          },
        });
        setSignUpStatus({ email: input.email, confirming: true });
      } catch (error: unknown) {
        throw new Error(getAuthErrorMessage(error));
      }
    },
    [setSignUpStatus],
  );

  const onConfirmSignUp = useCallback(
    async (input: ConfirmSignUpInput) => {
      try {
        await confirmSignUp({
          username: input.email,
          confirmationCode: String(input.authCode),
        });
        clearSignUpStatus();
      } catch (error: unknown) {
        throw new Error(getAuthErrorMessage(error));
      }
    },
    [clearSignUpStatus],
  );

  const onResetPassWord = useCallback(async (input: ResetPassWordInput) => {
    try {
      await resetPassword({
        username: input.email,
      });
    } catch (error: unknown) {
      throw new Error(getAuthErrorMessage(error));
    }
  }, []);

  const onConfirmResetPassWord = useCallback(
    async (input: ConfirmResetPassWordInput) => {
      try {
        await confirmResetPassword({
          username: input.email,
          confirmationCode: String(input.authCode),
          newPassword: input.newPassword,
        });
      } catch (error: unknown) {
        throw new Error(getAuthErrorMessage(error));
      }
    },
    [],
  );

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        isLoadingUser: meLoading,
        authError: meError,
        signUpStatus,
        signIn: onSignIn,
        signOut: onSignOut,
        resetPassWord: onResetPassWord,
        signUp: onSignUp,
        confirmSignUp: onConfirmSignUp,
        resendSignUp: onResendSignUp,
        confirmResetPassWord: onConfirmResetPassWord,
        refetchMe,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };
